import React, { useState } from "react";
import { Phone, Send } from "lucide-react";
import axios from "axios";
import { getAttributionFields } from "../utils/utm";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ROLE_OPTIONS = [
  "Landman", "Oil and Gas Landman", "Oil and Gas Lease Negotiator",
  "Right-of-Way Agent", "Pipeline Right-of-Way Agent", "Transmission Right-of-Way Agent",
  "Utility Right-of-Way Agent", "Landowner Negotiator", "Acquisition Agent",
  "Renewable Land Agent", "Solar Lease Negotiator", "Wind Lease Negotiator",
  "Battery Storage / BESS Land Agent", "Title / Abstracting", "Title Researcher / Abstractor",
  "Title Curative", "Title Curative Specialist",
  "GIS / Mapping", "Project Manager", "Field Coordinator", "Permitting Support",
  "Document Control", "Field Access / Survey Support Workflows",
  "Pipeline / Midstream Land Professional", "Transmission / Utility Land Professional",
  "Mining / Natural Resources Land Professional", "Other",
];

const PROJECT_TYPE_OPTIONS = [
  "Pipeline / Midstream", "Transmission / Utility", "Oil and Gas Leasing",
  "Renewable Energy", "Solar", "Wind",
  "Battery Storage / BESS", "Renewable / Other", "Right-of-Way / Corridor",
  "Development / Greenfield", "Title / Curative", "GIS / Mapping Support",
  "Field Access / Survey Support Workflows", "Mining / Natural Resources",
  "PathScore Support", "AreaScore Support", "Other",
];

const SUPPORT_LEVELS = [
  "One professional",
  "Several professionals",
  "A complete project team",
  "Managed land project support",
  "Not sure. Help me determine the need",
];

const ENGAGEMENT_OPTIONS = [
  "Short-term assignment", "Long-term assignment", "Project-specific assignment",
  "Supplemental staffing", "Full project management",
];

const SENIORITY_OPTIONS = ["Senior", "Mid-level", "Junior", "Open"];
const NUM_OPTIONS = ["1", "2", "3", "4", "5", "6-10", "11-20", "20+"];
const WORK_MODE_OPTIONS = ["Field", "Remote", "Hybrid", "Flexible"];
const MIN_YEARS_OPTIONS = ["No minimum", "1+", "3+", "5+", "10+", "15+"];

export const ClientTalentRequestForm = () => {
  const [form, setForm] = useState({
    company_name: "", contact_first_name: "", contact_last_name: "",
    business_email: "", phone: "",
    project_state: "", project_city: "", project_county_region: "",
    additional_states: "", project_address: "", work_mode: "",
    num_professionals: "", start_date: "", duration: "",
    support_level: "",
    min_years_experience: "", seniority: "", required_industry_experience: "",
    required_certifications: "", travel_expectations: "", schedule_expectations: "",
    special_requirements: "",
    weekly_hours: "", target_end_date: "", budget_range: "", notes: "",
  });
  const [roles, setRoles] = useState([]);
  const [projectTypes, setProjectTypes] = useState([]);
  const [engagementTypes, setEngagementTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState(null);

  const set = (e) => setForm((p) => ({ ...p, [e.target.name]: e.target.value }));
  const toggle = (setter, list, v) => setter(list.includes(v) ? list.filter((x) => x !== v) : [...list, v]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (roles.length === 0) { setError("Please select at least one position or specialty."); return; }
    if (!form.support_level) { setError("Please select the level of support you need."); return; }
    setLoading(true);
    setError(null);
    try {
      await axios.post(`${API}/talent-request`, {
        ...form,
        roles_needed: roles,
        project_types: projectTypes,
        engagement_types: engagementTypes,
        source_url: window.location.href,
        ...getAttributionFields(),
      });
      setSubmitted(true);
    } catch (err) {
      setError(err.response?.data?.detail || "Something went wrong. Please try again or call (817) 577-5024.");
    } finally {
      setLoading(false);
    }
  };

  if (submitted) {
    return (
      <div className="max-w-2xl mx-auto text-center py-16" data-testid="client-request-success">
        <h2 className="text-3xl font-bold text-white mb-6">Thank you.</h2>
        <p className="text-slate-300 text-lg leading-relaxed">
          Thank you. Onward has received your staffing request. Our team will review the project
          location, role requirements, timing, and available talent coverage before contacting you.
        </p>
        <p className="text-slate-400 mt-6">
          Prefer to talk through the need?{" "}
          <a href="tel:+18175775024" className="font-semibold underline" style={{ color: "#D97423" }}>
            Call Onward at (817) 577-5024
          </a>
        </p>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
      <form onSubmit={handleSubmit} className="space-y-6" data-testid="client-request-form">
        <div className="talent-pool__section-heading">Client Information</div>
        <Field label="Company Name *" name="company_name" required value={form.company_name} onChange={set} placeholder="Company" testId="client-company" />
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Contact First Name *" name="contact_first_name" required value={form.contact_first_name} onChange={set} placeholder="First name" testId="client-first-name" />
          <Field label="Contact Last Name *" name="contact_last_name" required value={form.contact_last_name} onChange={set} placeholder="Last name" testId="client-last-name" />
        </div>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Business Email *" name="business_email" type="email" required value={form.business_email} onChange={set} placeholder="you@company.com" testId="client-email" />
          <Field label="Phone Number *" name="phone" type="tel" required value={form.phone} onChange={set} placeholder="(555) 000-0000" testId="client-phone" />
        </div>

        <div className="talent-pool__section-heading">Project Location</div>
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Project State *" name="project_state" required value={form.project_state} onChange={set} placeholder="TX" testId="client-state" />
          <Field label="City or Nearest Community *" name="project_city" required value={form.project_city} onChange={set} placeholder="City" testId="client-city" />
        </div>
        <Field label="County, Region, Basin, or Market *" name="project_county_region" required value={form.project_county_region} onChange={set} placeholder="e.g. Tarrant County, Permian Basin, ERCOT South" testId="client-county" />
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Additional States" name="additional_states" value={form.additional_states} onChange={set} placeholder="e.g. OK, NM" testId="client-additional-states" />
          <SelectField label="Remote, Field, or Hybrid" name="work_mode" value={form.work_mode} onChange={set} options={WORK_MODE_OPTIONS} testId="client-work-mode" />
        </div>
        <Field label="Project Address or General Location" name="project_address" value={form.project_address} onChange={set} placeholder="Optional" testId="client-address" />

        <div className="talent-pool__section-heading">Role Requirements</div>
        <p className="text-slate-500 text-xs -mt-2">Position or specialty needed *. Select one or more.</p>
        <div className="grid sm:grid-cols-2 gap-2">
          {ROLE_OPTIONS.map((r) => (
            <label key={r} className="flex items-center gap-2 talent-pool__check">
              <input
                type="checkbox"
                checked={roles.includes(r)}
                onChange={() => toggle(setRoles, roles, r)}
                data-testid={`client-role-${r.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
              />
              <span className="text-slate-200 text-sm">{r}</span>
            </label>
          ))}
        </div>
        <div className="grid sm:grid-cols-3 gap-4">
          <SelectField label="Number of Professionals *" name="num_professionals" required value={form.num_professionals} onChange={set} options={NUM_OPTIONS} testId="client-num" />
          <Field label="Desired Start Date *" name="start_date" type="date" required value={form.start_date} onChange={set} testId="client-start-date" />
          <Field label="Estimated Assignment Duration *" name="duration" required value={form.duration} onChange={set} placeholder="e.g. 3 months" testId="client-duration" />
        </div>

        <div className="talent-pool__section-heading">Project Type</div>
        <div className="grid sm:grid-cols-2 gap-2">
          {PROJECT_TYPE_OPTIONS.map((pt) => (
            <label key={pt} className="flex items-center gap-2 talent-pool__check">
              <input type="checkbox" checked={projectTypes.includes(pt)} onChange={() => toggle(setProjectTypes, projectTypes, pt)} data-testid={`client-ptype-${pt.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`} />
              <span className="text-slate-200 text-sm">{pt}</span>
            </label>
          ))}
        </div>

        <div className="talent-pool__section-heading">Staffing Structure</div>
        <fieldset>
          <legend className="block text-sm font-medium text-slate-300 mb-2">What level of support do you need? *</legend>
          <div className="space-y-2">
            {SUPPORT_LEVELS.map((s) => (
              <label key={s} className="flex items-center gap-2 talent-pool__check">
                <input
                  type="radio" name="support_level" value={s}
                  checked={form.support_level === s} onChange={set}
                  style={{ accentColor: "#BF5700" }}
                  data-testid={`client-support-${s.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`}
                />
                <span className="text-slate-200 text-sm">{s}</span>
              </label>
            ))}
          </div>
        </fieldset>

        <div className="talent-pool__section-heading">Experience &amp; Qualifications</div>
        <div className="grid sm:grid-cols-2 gap-4">
          <SelectField label="Minimum Years of Experience" name="min_years_experience" value={form.min_years_experience} onChange={set} options={MIN_YEARS_OPTIONS} testId="client-min-years" />
          <SelectField label="Seniority Level" name="seniority" value={form.seniority} onChange={set} options={SENIORITY_OPTIONS} testId="client-seniority" />
        </div>
        <Field label="Required Industry Experience" name="required_industry_experience" value={form.required_industry_experience} onChange={set} placeholder="e.g. Transmission, Solar, Pipeline" testId="client-industry-exp" />
        <Field label="Required Certifications or Qualifications" name="required_certifications" value={form.required_certifications} onChange={set} placeholder="e.g. SR/WA, Notary" testId="client-certs" />
        <div className="grid sm:grid-cols-2 gap-4">
          <Field label="Travel Expectations" name="travel_expectations" value={form.travel_expectations} onChange={set} placeholder="e.g. Local, regional, overnight" testId="client-travel" />
          <Field label="Schedule Expectations" name="schedule_expectations" value={form.schedule_expectations} onChange={set} placeholder="e.g. Full-time, 5/2 rotation" testId="client-schedule" />
        </div>
        <Field label="Special Project Requirements" name="special_requirements" value={form.special_requirements} onChange={set} placeholder="Optional" testId="client-special" />

        <div className="talent-pool__section-heading">Engagement Details</div>
        <div className="grid sm:grid-cols-2 gap-2">
          {ENGAGEMENT_OPTIONS.map((en) => (
            <label key={en} className="flex items-center gap-2 talent-pool__check">
              <input type="checkbox" checked={engagementTypes.includes(en)} onChange={() => toggle(setEngagementTypes, engagementTypes, en)} data-testid={`client-engagement-${en.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}`} />
              <span className="text-slate-200 text-sm">{en}</span>
            </label>
          ))}
        </div>
        <div className="grid sm:grid-cols-3 gap-4">
          <Field label="Estimated Weekly Hours" name="weekly_hours" value={form.weekly_hours} onChange={set} placeholder="e.g. 40" testId="client-hours" />
          <Field label="Target End Date (if known)" name="target_end_date" type="date" value={form.target_end_date} onChange={set} testId="client-end-date" />
          <Field label="Budget Range (optional)" name="budget_range" value={form.budget_range} onChange={set} placeholder="Optional" testId="client-budget" />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-300 mb-1.5">Additional Notes</label>
          <textarea
            name="notes" rows={4} value={form.notes} onChange={set}
            data-testid="client-notes"
            className="w-full bg-slate-900/60 border border-slate-600 rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:outline-none"
            placeholder="Anything else Onward should know about the project or the need."
          />
        </div>

        {error && <p className="text-red-400 text-sm" data-testid="client-error">{error}</p>}

        <button
          type="submit"
          disabled={loading}
          className="ols-btn-primary w-full justify-center text-lg py-4"
          style={{ display: "flex" }}
          data-testid="client-submit"
        >
          {loading ? "Submitting..." : (<><Send className="mr-2 h-5 w-5" /> Send Project Request</>)}
        </button>

        <p className="text-center text-slate-400 text-sm" data-testid="client-phone-option">
          Prefer to talk through the need?{" "}
          <a href="tel:+18175775024" data-testid="client-request-phone" className="font-semibold underline inline-flex items-center gap-1" style={{ color: "#C8102E" }}>
            <Phone className="h-3.5 w-3.5" /> Call Onward at (817) 577-5024
          </a>
        </p>
        <p className="text-slate-400 text-xs leading-relaxed" data-testid="client-request-privacy-note">Do not include confidential project or title data here. Ask Onward about an NDA and file exchange during follow-up. <a href="/talent-network/privacy" className="underline" data-testid="client-request-privacy-link">Talent profile privacy notice</a>. For questions about project-request information, contact <a href="mailto:info@onwardland.com" className="underline" data-testid="client-request-privacy-email">info@onwardland.com</a>.</p>
      </form>
    </div>
  );
};

const Field = ({ label, name, type = "text", required, value, onChange, placeholder, testId }) => (
  <div>
    <label className="block text-sm font-medium text-slate-300 mb-1.5">{label}</label>
    <input
      name={name} type={type} required={required} value={value} onChange={onChange}
      placeholder={placeholder} data-testid={testId}
      className="w-full bg-slate-900/60 border border-slate-600 rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:outline-none"
      style={type === "date" ? { colorScheme: "dark" } : undefined}
    />
  </div>
);

const SelectField = ({ label, name, required, value, onChange, options, testId }) => (
  <div>
    <label className="block text-sm font-medium text-slate-300 mb-1.5">{label}</label>
    <select
      name={name} required={required} value={value} onChange={onChange} data-testid={testId}
      className="w-full bg-slate-900/60 border border-slate-600 rounded-lg px-4 py-3 text-white focus:outline-none"
    >
      <option value="">Select</option>
      {options.map((o) => <option key={o} value={o}>{o}</option>)}
    </select>
  </div>
);

export default ClientTalentRequestForm;
